import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;

function TransactionModal({ open, onClose, selectedCashBook, onSuccess, relatedIOU , editData = null }) {

    const [formData, setFormData] = useState({
        amount: "",
        description: "",
        attachments: []
    });

    const [previewImages, setPreviewImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [localTransactions, setLocalTransactions] = useState([]);

    const fileInputRef = useRef(null);
    const token = sessionStorage.getItem("token");
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    const role = user.role || "";
    const isNormalUser = role === "Farm User" || role === "Outlet User";
    const isIOUMode = Boolean(relatedIOU);

    console.log("RELATED IOU:", relatedIOU);

    useEffect(() => {
        return () => {
            previewImages.forEach(img => {
                if (img) URL.revokeObjectURL(img);
            });
        };
    }, [previewImages]);


    useEffect(() => {

        if (!open) {

            setFormData({
                amount: "",
                description: "",
                attachments: []
            });

            setPreviewImages([]);

            // ONLY RESET WHEN MODAL FULLY CLOSES
            setTimeout(() => {
                setLocalTransactions([]);
            }, 100);
        }

    }, [open]);

    useEffect(() => {
        if (open) {
            setLoading(false);
        }
    }, [open]);

    useEffect(() => {

        if (editData) {

            setFormData({
                amount: editData.amount || "",
                description: editData.description || "",
                attachments: []
            });

            const formattedAttachments =
                (editData.attachments || []).map(file => {

                    if (!file) return null;

                    // Existing uploaded file
                    if (typeof file === "string") {

                        // already full url
                        if (file.startsWith("http")) {
                            return file;
                        }

                        return `${IMAGE_BASE_URL}/${file}`;
                    }

                    return file;
                });

            setPreviewImages(formattedAttachments);
        }else {
            setFormData({
                amount: "",
                description: "",
                attachments: []
            });

            setPreviewImages([]);
        }

    }, [editData]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const normalizeMoney = (value) => {

        const num = parseFloat(value);

        if (isNaN(num)) {
            return 0;
        }

        return num;
    };

    const validTypes = [
        "image/jpeg",
        "image/png",
        "image/webp",
        "application/pdf"
    ];

    const handleFiles = async (files) => {

        const filtered = files.filter(file => validTypes.includes(file.type));

        if (filtered.length !== files.length) {
            toast.error("Only images and PDFs allowed");
        }

        const compressed = await Promise.all(
            filtered.map(async file => {
                if (file.type.startsWith("image/")) {
                    return await imageCompression(file, {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 800
                    });
                }
                return file;
            })
        );

        setFormData(prev => ({
            ...prev,
            attachments: [...prev.attachments, ...compressed]
        }));

        const previews = compressed.map(file =>
            file.type.startsWith("image/")
                ? URL.createObjectURL(file)
                : null
        );

        setPreviewImages(prev => [...prev, ...previews]);
    };

    const handleSubmit = async () => {

        if (loading) return;

        if (!formData.amount || formData.amount <= 0) {
            return toast.error("Amount required");
        }
        setLoading(true);
        try {

            const form = new FormData();

            if(isNormalUser){
                form.append("user_reimbursement", "true");
                form.append("type", "Payment");
            }else{
                form.append("cash_book", selectedCashBook._id);
                form.append("category", selectedCashBook.category._id);
                form.append("type", selectedCashBook.type);
            }

            form.append("amount", normalizeMoney(formData.amount));
            form.append("description", formData.description);

            if (relatedIOU) {
                form.append("related_iou", relatedIOU);
            }

            formData.attachments.forEach(file => {
                form.append("attachments", file);
            });

            let res;

            if (editData) {

                res = await axios.put(
                    `${API_BASE_URL}/pettycash/update-simple-transaction/${editData._id}`,
                    form,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "multipart/form-data"
                        }
                    }
                );
            }else {
                 res = await axios.post(
                    `${API_BASE_URL}/pettycash/transaction/simple`,
                    form,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "multipart/form-data"
                        }
                    }
                );
            }

            const newTransaction = res.data.data;

            if (isIOUMode && !editData) {

                setLocalTransactions(prev => [
                    ...prev,
                    newTransaction
                ]);

                if (onSuccess) {
                    onSuccess({
                        transactions: [newTransaction]
                    });
                }

                setFormData({
                    amount: "",
                    description: "",
                    attachments: []
                });

                setPreviewImages([]);

                setTimeout(() => {

                    const amountInput =
                        document.querySelector(
                            'input[name="amount"]'
                        );

                    if (amountInput) {
                        amountInput.focus();
                    }

                }, 100);

            }

            // 🔥 NORMAL MODE → close
            else {

                if (onSuccess) {
                    onSuccess({
                        transactions: [newTransaction],
                        updated: !!editData
                    });
                }

                if (isNormalUser) {
                    onClose();
                }else{
                    onClose();
                }
            }

        } catch (err) {
            toast.error(err.response?.data?.message || "Error");
        }finally {
            setLoading(false);
        }
    };



    const total = localTransactions.reduce((sum, t) => sum + t.amount, 0);

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">

            <div className="bg-white rounded-xl shadow-2xl w-[520px] max-w-[95vw] max-h-[90vh] flex flex-col overflow-hidden">

                <div className="shrink-0 bg-green-700 text-white px-6 py-4 flex justify-between items-center">
                    <h2>Add Transaction</h2>
                    <button onClick={onClose}>×</button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar scroll-smooth">

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            Amount
                        </label>
                        <input
                            type="number"
                            step="any"
                            name="amount"
                            value={formData.amount}
                            onChange={handleChange}
                            className="w-full border px-4 py-2 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                            className="w-full border px-4 py-2 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            Attachments
                        </label>

                        <div
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => {
                                e.preventDefault();
                                handleFiles(Array.from(e.dataTransfer.files));
                            }}
                            onClick={() => fileInputRef.current.click()}
                            className="border-2 border-dashed border-gray-300 rounded-lg p-3 cursor-pointer hover:border-green-500 transition min-h-[120px]"
                        >

                            {previewImages.length === 0 && (
                                <div className="text-center text-gray-500 h-full flex items-center justify-center">
                                    <div>
                                        <p>
                                            Drag & Drop or{" "}
                                            <span className="text-green-600 font-semibold">
                                                Click to upload
                                            </span>
                                        </p>
                                        <p className="text-xs mt-1">
                                            Images / PDF (Max 5MB)
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-3">

                                {previewImages.map((img, i) => {
                                     const isPdf = typeof img === "string" && img.toLowerCase().includes(".pdf");

                                     return (
                                         <div key={i} className="relative group">

                                             {!isPdf && img ? (
                                                 <img
                                                     src={img}
                                                     alt=""
                                                     className="w-20 h-20 object-cover rounded-lg border"
                                                 />
                                             ) : (
                                                 <div className="w-20 h-20 flex items-center justify-center bg-gray-200 rounded-lg text-xs border">
                                                     📄 PDF
                                                 </div>
                                             )}

                                             <button
                                                 onClick={(e) => {
                                                     e.stopPropagation();

                                                     const newFiles = [...formData.attachments];
                                                     const newPrev = [...previewImages];

                                                     newFiles.splice(i, 1);
                                                     newPrev.splice(i, 1);

                                                     setFormData({
                                                         ...formData,
                                                         attachments: newFiles
                                                     });

                                                     setPreviewImages(newPrev);
                                                 }}
                                                 className="absolute -top-2 -right-2 bg-red-600 text-white w-5 h-5 text-xs rounded-full opacity-0 group-hover:opacity-100 transition"
                                             >
                                                 ×
                                             </button>

                                         </div>
                                     )
                                })}

                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        fileInputRef.current.click();
                                    }}
                                    className="w-20 h-20 flex items-center justify-center border-2 border-dashed rounded-lg text-gray-500 hover:border-green-500 hover:text-green-600 transition cursor-pointer"
                                >
                                    <span className="text-2xl">+</span>
                                </div>

                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*,.pdf"
                                className="hidden"
                                onChange={e => handleFiles(Array.from(e.target.files))}
                            />
                        </div>
                    </div>

                    {isIOUMode && localTransactions.length > 0 && (
                        <div className="border-t pt-3">
                            <h4 className="font-semibold">Added Expenses</h4>

                            {localTransactions.map((tx, i) => (
                                <div key={i} className="flex justify-between text-sm">
                                    <span>Rs {tx.amount}</span>
                                    <span>{tx.description}</span>
                                </div>
                            ))}

                            <div className="text-right font-bold mt-2">
                                Total: Rs {total}
                            </div>
                        </div>
                    )}

                    {/* ACTIONS */}
                    <div className="shrink-0 flex justify-end gap-3 p-5 border-t bg-white">

                        <button
                            onClick={onClose}
                            className="bg-gray-300 px-4 py-2 rounded"
                        >
                            Cancel
                        </button>

                        {/* 🔥 IOU MODE */}
                        {isIOUMode ? (
                            <>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading || !formData.amount}
                                    className="bg-green-700 text-white px-4 py-2 rounded"
                                >
                                    Add
                                </button>

                                <button
                                    onClick={() => {

                                        if (localTransactions.length === 0) {
                                            toast.error("Add at least one transaction");
                                            return;
                                        }

                                        onClose();
                                    }}
                                    className="bg-blue-600 text-white px-4 py-2 rounded"
                                >
                                    Done
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={loading}
                                className={`px-4 py-2 rounded ${
                                    loading
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : "bg-green-700 text-white"
                                }`}
                            >
                                {loading ? "Saving..." : "Save"}
                            </button>
                        )}

                    </div>

                </div>
            </div>
        </div>
    );
}

export default TransactionModal;

